import PopUpPage from 'Components/PopUpPage';
import cls from 'classnames';
import { actions } from 'core/redux/features/products';
import { selectAllProducts } from 'core/redux/features/products/reducers/products';
import { IProduct } from 'core/redux/features/products/types';
import { selectDeviceType } from 'core/redux/reducers';
import { history } from 'core/redux/stores';
import { voidCallback } from 'core/utils';
import { useEffect, useRef, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { Portal } from 'react-portal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useTrans } from '../../../../core/hooks';
import { useScrollToProductListView } from '../../hooks';
import { activeFilterToggled, useFilterState } from '../store';
import { ReactComponent as SearchIcon } from './search-icon.svg';

function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getSuggestions(products: IProduct[], value) {
  const escapedValue = escapeRegexCharacters(value.trim());

  if (escapedValue === '') {
    return [];
  }

  const regex = new RegExp('^' + escapedValue, 'i');

  return products.filter(item => regex.test(item.name));
}

function getSuggestionValue(suggestion) {
  return suggestion.name;
}

function renderSuggestion(suggestion) {
  return <span>{suggestion.name}</span>;
}

export const SearchFilter = ({ forPopup = false }: { forPopup?: boolean }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const products = useSelector(selectAllProducts);
  const deviceType = useSelector(selectDeviceType);

  const { filterDispatch, filterState } = useFilterState();
  const [suggestions, setSuggestions] = useState<IProduct[]>([]);
  const [inputVal, setInputVal] = useState<string>('');

  const scrollToListView = useScrollToProductListView();

  // popup is only available in mobile
  // so we have to check what is the device type
  const forPopupOnly = forPopup && deviceType === 'mobile';

  const inputRef = useRef<HTMLInputElement>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // auto focus input if the search box is used in popup
    // and the filter search is active
    if (
      forPopupOnly &&
      inputRef.current &&
      filterState.activeFilter === 'search'
    ) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [deviceType, filterState.activeFilter, forPopupOnly]);

  const t = useTrans();
  const inputProps = {
    placeholder: t('search'),
    value: inputVal,
    autoFocus: forPopupOnly,
    ref: inputRef,
  };
  return (
    <div
      className="field"
      onClick={e => {
        e.preventDefault();
        if (deviceType === 'mobile' && !forPopupOnly) {
          filterDispatch(activeFilterToggled('search'));
          if (
            filterState.activeFilter !== 'search' ||
            location.hash.indexOf('search') < 0
          ) {
            history.push('#search');
          }
        }
      }}
    >
      <div className="control has-icons-left">
        {!forPopup && deviceType === 'mobile' ? (
          <input
            {...inputProps}
            onChange={voidCallback}
            className="input no-event"
          />
        ) : (
          <Autosuggest
            suggestions={suggestions}
            focusInputOnSuggestionClick={false}
            alwaysRenderSuggestions={false}
            highlightFirstSuggestion={true}
            onSuggestionSelected={(_, { suggestion }) => {
              dispatch(actions.activateProduct(suggestion));
              scrollToListView();

              // suggestion is selected and we are in mobile
              // we have to redirect to use to product page
              // and close the search box
              if (forPopup && deviceType === 'mobile') {
                history.push(suggestion.route);
                filterDispatch(activeFilterToggled(''));
              }
            }}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            shouldRenderSuggestions={val => {
              return val && val.length > 0;
            }}
            onSuggestionsFetchRequested={({ value }) => {
              setSuggestions(getSuggestions(Object.values(products), value));
            }}
            onSuggestionsClearRequested={() => {
              setSuggestions([]);
            }}
            inputProps={{
              ...inputProps,
              onChange: (_, { newValue }) => {
                setInputVal(newValue);
              },
            }}
            renderInputComponent={({ className, ...inputProps }) => {
              return (
                <input
                  {...inputProps}
                  type="text"
                  className={cls(className, 'input')}
                />
              );
            }}
          />
        )}

        <div className="icon is-small is-left">
          <SearchIcon style={{ width: 15 }} className="input-over__icon" />
        </div>
      </div>
    </div>
  );
};

export const SearchFilterPopup = () => {
  const { filterState, filterDispatch } = useFilterState();
  const deviceType = useSelector(selectDeviceType);

  const showPopup =
    deviceType === 'mobile' && filterState.activeFilter === 'search';
  return (
    <Portal node={document.body}>
      <PopUpPage backdrop={false} hide={!showPopup} className="search-popup">
        <div className="filters--search-pop">
          <div className="filters">
            <div className="box">
              <div className="box-header">
                <div
                  className="eb eb-close"
                  onClick={_ => {
                    if (filterState.activeFilter !== '') {
                      filterDispatch(activeFilterToggled(''));
                      history.goBack();
                    }
                  }}
                />
              </div>
              <SearchFilter forPopup={true} />
            </div>
          </div>
        </div>
      </PopUpPage>
    </Portal>
  );
};
